import React, { useEffect, useState, useCallback } from 'react';
import { X, Clock, CheckCircle, XCircle, Play, Brain, Zap, MessageSquare, RefreshCw } from 'lucide-react';

interface TaskInfo {
  task_id?: string;
  status: string;
  name: string;
  description?: string;
  started_at?: string;
  completed_at?: string;
}

interface ThoughtLog {
  id: string;
  execution_id: string;
  task_id?: string;
  node_id?: string;
  thought_type: string;
  message: string;
  agent_id?: string;
  agent_name?: string;
  timestamp: string;
}

interface NodeDetailPanelProps {
  nodeId: string;
  nodeLabel: string;
  executionId?: string;
  taskInfo?: TaskInfo;
  onClose: () => void;
}

export const NodeDetailPanel: React.FC<NodeDetailPanelProps> = ({
  nodeId,
  nodeLabel,
  executionId,
  taskInfo,
  onClose,
}) => {
  const [thoughts, setThoughts] = useState<ThoughtLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchThoughts = useCallback(async () => {
    if (!executionId) return;
    setLoading(true);
    setFetchError(null);
    try {
      let token: string | null = null;
      try {
        const raw = localStorage.getItem('auth-storage');
        if (raw) token = JSON.parse(raw)?.state?.token ?? null;
      } catch { /* ignore */ }

      const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/$/, '');
      let url = `${apiBase}/api/v1/workflow-execution/execute/${executionId}/thought-logs`;
      if (taskInfo?.task_id) {
        url += `?task_id=${encodeURIComponent(taskInfo.task_id)}`;
      }

      console.debug('[NodeDetailPanel] fetching', url, 'token:', token ? 'present' : 'missing');
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        console.debug('[NodeDetailPanel] got', data.length, 'thoughts');
        setThoughts(data);
      } else {
        const text = await res.text().catch(() => res.statusText);
        setFetchError(`${res.status}: ${text}`);
        console.error('[NodeDetailPanel] fetch failed', res.status, text);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setFetchError(msg);
      console.error('[NodeDetailPanel] fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [executionId, taskInfo?.task_id]);

  useEffect(() => {
    fetchThoughts();
  }, [fetchThoughts]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'in_progress': return <Play className="w-4 h-4 text-blue-600" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      completed: 'bg-green-100 text-green-800',
      in_progress: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-gray-100 text-gray-700',
      paused: 'bg-yellow-100 text-yellow-800',
    };
    return classes[status] || 'bg-gray-100 text-gray-700';
  };

  const getThoughtIcon = (type: string) => {
    switch (type) {
      case 'thought':
      case 'thinking': return <Brain className="w-3 h-3 text-purple-500" />;
      case 'action': return <Zap className="w-3 h-3 text-orange-500" />;
      case 'observation': return <MessageSquare className="w-3 h-3 text-blue-500" />;
      case 'decision': return <Zap className="w-3 h-3 text-fuchsia-500" />;
      case 'error': return <XCircle className="w-3 h-3 text-red-500" />;
      default: return <MessageSquare className="w-3 h-3 text-gray-400" />;
    }
  };

  const getThoughtBgColor = (type: string) => {
    switch (type) {
      case 'thought':
      case 'thinking': return 'bg-purple-50 border-purple-100';
      case 'action': return 'bg-orange-50 border-orange-100';
      case 'observation': return 'bg-blue-50 border-blue-100';
      case 'decision': return 'bg-fuchsia-50 border-fuchsia-100';
      case 'error': return 'bg-red-50 border-red-100';
      default: return 'bg-gray-50 border-gray-100';
    }
  };

  const duration = taskInfo?.started_at && taskInfo?.completed_at
    ? Math.round((new Date(taskInfo.completed_at).getTime() - new Date(taskInfo.started_at).getTime()) / 1000)
    : null;

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200 w-80 min-w-[300px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm truncate">{nodeLabel}</h3>
          <p className="text-xs text-gray-500 truncate">Node: {nodeId}</p>
        </div>
        <button
          onClick={fetchThoughts}
          title="Refresh thoughts"
          className="ml-1 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onClose}
          className="ml-1 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Task metadata */}
        {taskInfo ? (
          <div className="p-4 border-b border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Task Details</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Status</span>
                <div className="flex items-center space-x-1">
                  {getStatusIcon(taskInfo.status)}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(taskInfo.status)}`}>
                    {taskInfo.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              {taskInfo.description && (
                <div>
                  <span className="text-xs text-gray-500">Description</span>
                  <p className="text-xs text-gray-700 mt-0.5">{taskInfo.description}</p>
                </div>
              )}
              {taskInfo.started_at && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Started</span>
                  <span className="text-xs text-gray-700">{new Date(taskInfo.started_at).toLocaleTimeString()}</span>
                </div>
              )}
              {taskInfo.completed_at && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Completed</span>
                  <span className="text-xs text-gray-700">{new Date(taskInfo.completed_at).toLocaleTimeString()}</span>
                </div>
              )}
              {duration !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Duration</span>
                  <span className="text-xs text-gray-700">{duration}s</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 border-b border-gray-100">
            <p className="text-xs text-gray-400 italic">No task data available for this node</p>
          </div>
        )}

        {/* Thoughts & Actions */}
        <div className="p-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Thoughts & Actions
            {thoughts.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-fuchsia-100 text-fuchsia-700 rounded-full font-normal">
                {thoughts.length}
              </span>
            )}
          </h4>

          {loading ? (
            <div className="text-center py-6">
              <div className="w-5 h-5 border-2 border-fuchsia-300 border-t-fuchsia-600 rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-gray-400">Loading logs…</p>
            </div>
          ) : fetchError ? (
            <div className="rounded-lg bg-red-50 border border-red-100 p-3">
              <p className="text-xs font-medium text-red-700 mb-1">Failed to load thoughts</p>
              <p className="text-xs text-red-600 break-all">{fetchError}</p>
              <button onClick={fetchThoughts} className="mt-2 text-xs text-red-700 underline">Retry</button>
            </div>
          ) : thoughts.length === 0 ? (
            <div className="text-center py-6">
              <Brain className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-400">No thoughts recorded yet</p>
              <p className="text-xs text-gray-300 mt-1">Thoughts will appear here once the task runs</p>
            </div>
          ) : (
            <div className="space-y-2">
              {thoughts.map((thought) => (
                <div
                  key={thought.id}
                  className={`rounded-lg border p-2.5 ${getThoughtBgColor(thought.thought_type)}`}
                >
                  <div className="flex items-center space-x-1.5 mb-1">
                    {getThoughtIcon(thought.thought_type)}
                    <span className="text-xs font-medium text-gray-700 capitalize">{thought.thought_type}</span>
                    <span className="text-xs text-gray-400 ml-auto">
                      {new Date(thought.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{thought.message}</p>
                  {thought.agent_name && (
                    <p className="text-xs text-gray-400 mt-1">— {thought.agent_name}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
